import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Target, TrendingUp, User, Dumbbell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';

type AppLayoutProps = { children: React.ReactNode };

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { state, dispatch } = useApp();

  const isActive = (path: string) => location.pathname === path;

  // Sync Profile from DB on load/reload
  useEffect(() => {
    const syncProfile = async () => {
      // Only sync if authenticated, user marked as having profile, but AppContext is empty
      if (isAuthenticated && user?.hasCompletedProfile && !state.userProfile) {
        try {
          const res = await fetch('/api/profile', { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            if (data.success && data.data?.profile) {
              dispatch({ type: 'SET_USER_PROFILE', payload: data.data.profile });
            }
          }
        } catch (error) {
          console.error('Failed to sync profile', error);
        }
      }
    };

    syncProfile();
  }, [isAuthenticated, user, state.userProfile, dispatch]);

  const navItems = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/exercises', icon: Dumbbell, label: 'Exercises', tourId: 'library' },
    { path: '/goals', icon: Target, label: 'Goals' },
    { path: '/progress', icon: TrendingUp, label: 'Progress' },
    { path: '/profile', icon: User, label: 'Profile' },
  ];

  return (
    <div className="h-screen w-full overflow-hidden flex bg-black">
      {/* Sidebar Fixa */}
      <div className="w-20 h-screen bg-black border-r border-white/10 flex flex-col items-center py-8">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-12 h-12 flex items-center justify-center mb-6 transition-all duration-300 ${active
                  ? 'text-white bg-white/10 rounded-lg'
                  : 'text-gray-500 hover:text-white hover:bg-white/5 rounded-lg'
                }`}
              title={item.label}
              data-tour={item.tourId}
            >
              <Icon size={24} />
            </button>
          );
        })}
      </div>

      {/* Área de Conteúdo */}
      <div className="flex-1 h-screen overflow-y-auto">
        {children}
      </div>
    </div>
  );
};

